import { createTheme } from '@mui/material/styles';

// Custom color palette
const theme = createTheme({
  palette: {
    primary: {
      main: '#1e3a8a',
      light: '#3e5aa8',
      dark: '#0c2365',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#c9995c',
      light: '#ddb37f',
      dark: '#b17f3e',
      contrastText: '#ffffff',
    },
    success: {
      main: '#2e7d32',
      light: '#4caf50',
      dark: '#1b5e20',
    },
    error: {
      main: '#d32f2f',
      light: '#ef5350',
      dark: '#c62828',
    },
    background: {
      default: '#f7f9fc',
      paper: '#ffffff',
    },
    text: {
      primary: '#1c2b41',
      secondary: '#5a6a82',
    },
  },
  typography: {
    fontFamily: "'Inter', 'Roboto', 'Arial', sans-serif",
    h1: {
      fontFamily: "'Playfair Display', serif",
      fontWeight: 700,
      letterSpacing: '0.02em',
    },
    h2: {
      fontFamily: "'Playfair Display', serif",
      fontWeight: 700,
      letterSpacing: '0.01em',
    },
    h3: {
      fontFamily: "'Playfair Display', serif",
      fontWeight: 600,
      letterSpacing: '0.005em',
    },
    h4: {
      fontFamily: "'Playfair Display', serif",
      fontWeight: 600,
      letterSpacing: '0.005em',
    },
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
    button: {
      fontWeight: 600,
      textTransform: 'none',
    },
    subtitle1: {
      fontWeight: 500,
      letterSpacing: '0.01em',
    },
    subtitle2: {
      fontWeight: 500,
      letterSpacing: '0.01em',
    },
    body1: {
      lineHeight: 1.7,
    },
    body2: {
      lineHeight: 1.6,
    },
  },
  shape: {
    borderRadius: 8,
  },
  shadows: [
    'none',
    '0px 2px 6px rgba(0, 0, 0, 0.05)',
    '0px 3px 8px rgba(0, 0, 0, 0.07)',
    '0px 4px 10px rgba(0, 0, 0, 0.08)',
    '0px 5px 12px rgba(0, 0, 0, 0.09)',
    '0px 6px 14px rgba(0, 0, 0, 0.1)',
    '0px 7px 16px rgba(0, 0, 0, 0.11)',
    '0px 8px 18px rgba(0, 0, 0, 0.12)',
    '0px 9px 20px rgba(0, 0, 0, 0.13)',
    '0px 10px 22px rgba(0, 0, 0, 0.14)',
    '0px 11px 24px rgba(0, 0, 0, 0.15)',
    '0px 12px 26px rgba(0, 0, 0, 0.16)',
    '0px 13px 28px rgba(0, 0, 0, 0.17)',
    '0px 14px 30px rgba(0, 0, 0, 0.18)',
    '0px 15px 32px rgba(0, 0, 0, 0.19)',
    '0px 16px 34px rgba(0, 0, 0, 0.2)',
    '0px 17px 36px rgba(0, 0, 0, 0.21)',
    '0px 18px 38px rgba(0, 0, 0, 0.22)',
    '0px 19px 40px rgba(0, 0, 0, 0.23)',
    '0px 20px 42px rgba(0, 0, 0, 0.24)',
    '0px 21px 44px rgba(0, 0, 0, 0.25)',
    '0px 22px 46px rgba(0, 0, 0, 0.26)',
    '0px 23px 48px rgba(0, 0, 0, 0.27)',
    '0px 24px 50px rgba(0, 0, 0, 0.28)',
    '0px 25px 52px rgba(0, 0, 0, 0.29)',
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          boxShadow: 'none',
          padding: '10px 24px',
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            transform: 'translateY(-1px)',
          },
        },
        containedPrimary: {
          background: 'linear-gradient(135deg, #1e3a8a 0%, #3e5aa8 100%)',
          '&:hover': {
            background: 'linear-gradient(135deg, #1e3a8a 20%, #3e5aa8 100%)',
          },
        },
        containedSecondary: {
          background: 'linear-gradient(135deg, #c9995c 0%, #ddb37f 100%)',
          '&:hover': {
            background: 'linear-gradient(135deg, #c9995c 20%, #ddb37f 100%)',
          },
        },
        outlined: {
          borderWidth: '2px',
          '&:hover': {
            borderWidth: '2px',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.06)',
          borderRadius: 12,
          overflow: 'hidden',
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: '0 8px 30px rgba(0, 0, 0, 0.1)',
            transform: 'translateY(-3px)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          color: '#1c2b41',
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          transition: 'all 0.2s ease',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
            transform: 'scale(1.05)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            transition: 'all 0.2s ease',
            '&:hover': {
              boxShadow: '0 1px 5px rgba(0, 0, 0, 0.05)',
            },
            '&.Mui-focused': {
              boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
            },
          },
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRadius: '0 16px 16px 0',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          borderRadius: 16,
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          textDecoration: 'none',
          fontWeight: 500,
          transition: 'all 0.2s ease',
          '&:hover': {
            textDecoration: 'none',
          },
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          letterSpacing: '0.015em',
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          transition: 'all 0.2s ease',
        },
      },
    },
  },
});

export default theme; 